import React from "react";
import { ThemeProvider, StyledEngineProvider } from "@mui/material/styles";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { RecoilRoot } from "recoil";
import CssBaseline from "@mui/material/CssBaseline";
import LoginPage from "./pages/loginPage";
import ForgotPasswordPage from "./pages/forgotPasswordPage";
import ProtectedPages from "./pages/protectedPages";
import { QueryClient, QueryClientProvider } from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";
import { Analytics } from "@vercel/analytics/react";
import type { BeforeSendEvent } from "@vercel/analytics/react";
import { LOGIN_PAGE, FORGOT_PASSWORD_PAGE } from "./util/constants";
import { utils } from "@workspace/shared";
const { setPolyfills, theme } = utils;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

setPolyfills();

// URLs in this app can carry secrets in the query string — the bearer token
// (`?token=` deep-link/embed auth) and password-reset params (`guid`, `user_id`).
// Strip them before any event reaches Vercel so credentials/PII never leave the
// browser; the path is preserved so usage monitoring still works.
const SENSITIVE_QUERY_PARAMS = ["token", "guid", "user_id"];

const stripSensitiveParams = (event: BeforeSendEvent) => {
  try {
    const url = new URL(event.url);
    SENSITIVE_QUERY_PARAMS.forEach(key => url.searchParams.delete(key));
    return { ...event, url: url.toString() };
  } catch {
    return null; // drop the event entirely if the URL can't be parsed safely
  }
};

function App() {
  return (
    <RecoilRoot>
      <QueryClientProvider client={queryClient}>
        <ReactQueryDevtools initialIsOpen={false} />
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <Router>
              <Routes>
                <Route path={LOGIN_PAGE} element={<LoginPage />} />
                <Route path={FORGOT_PASSWORD_PAGE} element={<ForgotPasswordPage />} />
                <Route path={"/*"} element={<ProtectedPages />} />
              </Routes>
            </Router>
            <Analytics beforeSend={stripSensitiveParams} />
          </ThemeProvider>
        </StyledEngineProvider>
      </QueryClientProvider>
    </RecoilRoot>
  );
}

export default App;
